import axios from 'axios';
import authService from './authService';
import { getApiBaseUrl } from './apiConfig';
import type { ManagedEntityType } from './managedEntityApi';

const API_BASE = `${getApiBaseUrl()}/api/connections`;

/**
 * The other workspace on a connection, as `/outgoing`, `/incoming` and
 * `/pending` return it: an allowlisted slice, never the workspace row
 * (`services/connectionListPolicy.ts`). Add a field there before reading it here.
 */
export interface ConnectedWorkspace {
    id: string;
    type: ManagedEntityType;
    name: string;
    /** The workspace's own service provider profile, when it has one. */
    managedServiceProvider?: Array<{
        id: string;
        name: string;
        specialty?: string | null;
        profilePicture?: string | null;
    }>;
}

/** One show the viewer and a suggested workspace were both on. */
export interface SharedShow {
    eventId: string;
    name: string;
    /** ISO timestamp of the show's scheduled start. */
    date: string;
    billSize: number;
}

/**
 * A workspace the viewer has shared a bill with and is not yet connected to.
 * Built server-side by `services/sharedBillPolicy.ts`, which decides what may
 * be shown; nothing here should be re-derived on the client.
 */
/** What an Unclaimed act's card carries (issue #41 phase 1b, ADR-0042). */
export interface UnclaimedCard {
    /** The workspace that keeps the act's profile. Only it can send the invite. */
    maintainingEntityId: string;
    maintainerName: string;
    /** `invite`: you keep the profile, send it yourself. `suggest`: ask the keeper to. */
    action: 'invite' | 'suggest';
    /** Your own suggestion's state. Only ever "invited", never a refusal. */
    state: 'none' | 'waiting' | 'invited';
}

export interface SharedBillSuggestion {
    /** `workspace`: offer Connect. `unclaimed`: an act not on Showliner yet. */
    kind: 'workspace' | 'unclaimed';
    /** Null for an Unclaimed act, which has no workspace. */
    managedEntityId: string | null;
    entityType: 'performer' | 'venue' | 'service_provider';
    entityId: string;
    name: string;
    profilePicture: string | null;
    /** Most recent first, a few at most. `sharedShowCount` covers the rest. */
    sharedShows: SharedShow[];
    sharedShowCount: number;
    /** Set aside once with Not now, and brought back by a newer shared show (the first in `sharedShows`). */
    returning: boolean;
    /** Present exactly when `kind` is `unclaimed`. */
    unclaimed?: UnclaimedCard;
}

export interface SharedBillSuggestions {
    suggestions: SharedBillSuggestion[];
    total: number;
}
const withAuth = () => ({ headers: authService.getAuthHeader() });

/**
 * Workspace-to-workspace connection (symmetric, handshake).
 * Different from `followApi` — a connection requires mutual consent and grants
 * shared-event/roster access. A follow is asymmetric and subscribes to updates.
 */
export const connectionApi = {
    /**
     * Send a connection request. status='accepted' skips the handshake and is
     * refused (403 RECIPIENT_CONSENT_REQUIRED) unless the caller also holds
     * `connections.accept` on the recipient workspace; it then writes both
     * directions as accepted.
     */
    async requestConnection(
        initiatorId: string,
        recipientId: string,
        status: 'pending' | 'accepted' = 'pending',
    ): Promise<any> {
        const res = await axios.post(API_BASE, { initiatorId, recipientId, status }, withAuth());
        return res.data;
    },

    /** Accept a pending incoming connection request. `recipientId` is yours. */
    async acceptConnection(initiatorId: string, recipientId: string): Promise<any> {
        const res = await axios.post(
            `${API_BASE}/${initiatorId}/accept`,
            { recipientId },
            withAuth(),
        );
        return res.data;
    },

    /** Decline a pending incoming connection request. */
    async declineConnection(initiatorId: string, recipientId: string): Promise<void> {
        await axios.post(`${API_BASE}/${initiatorId}/decline`, { recipientId }, withAuth());
    },

    /** Disconnect from a currently-connected entity. */
    async disconnect(initiatorId: string, recipientId: string): Promise<void> {
        await axios.delete(API_BASE, { data: { initiatorId, recipientId }, ...withAuth() });
    },

    /** Entities this workspace has outgoing accepted connections to. */
    async getOutgoing(entityId: string): Promise<ConnectedWorkspace[]> {
        const res = await axios.get(`${API_BASE}/outgoing`, { params: { entityId }, ...withAuth() });
        return res.data;
    },

    /** Entities with accepted incoming connections to this workspace. */
    async getIncoming(entityId: string): Promise<ConnectedWorkspace[]> {
        const res = await axios.get(`${API_BASE}/incoming`, { params: { entityId }, ...withAuth() });
        return res.data;
    },

    /** Pending incoming connection requests. */
    async getPending(entityId: string): Promise<ConnectedWorkspace[]> {
        const res = await axios.get(`${API_BASE}/pending`, { params: { entityId }, ...withAuth() });
        return res.data;
    },

    /**
     * Directional status lookup for a single pair. Returns the raw connection
     * row (any status) or null. Use this to render the profile-button state
     * without pulling the full outgoing list.
     */
    async getConnection(
        initiatorId: string,
        recipientId: string,
    ): Promise<{
        id: string;
        initiatorId: string;
        recipientId: string;
        status: 'pending' | 'accepted' | 'blocked';
        settings?: Record<string, any>;
    } | null> {
        const res = await axios.get(`${API_BASE}/between`, {
            params: { initiatorId, recipientId },
            ...withAuth(),
        });
        return res.data || null;
    },

    /** Bidirectional connection check — true only when both directions are accepted. */
    async areConnected(entityId1: string, entityId2: string): Promise<boolean> {
        const res = await axios.get(`${API_BASE}/has`, {
            params: { entityId1, entityId2 },
            ...withAuth(),
        });
        return res.data.connected;
    },

    /** "You've shared a bill": Connection suggestions for this workspace (issue #41). */
    async getSuggestions(managedEntityId: string): Promise<SharedBillSuggestions> {
        const res = await axios.get(`${API_BASE}/suggestions/${managedEntityId}`, withAuth());
        return res.data;
    },

    /**
     * "Not now": set a suggestion aside for the whole workspace. Silent; it comes
     * back only when the two play a newer show together.
     */
    async notNowSuggestion(managedEntityId: string, suggestedEntityId: string): Promise<void> {
        await axios.post(`${API_BASE}/suggestions/${managedEntityId}/not-now`, { suggestedEntityId }, withAuth());
    },

    /** Not now for an Unclaimed act's card, keyed on the performer. */
    async notNowStubSuggestion(managedEntityId: string, performerId: string): Promise<void> {
        await axios.post(`${API_BASE}/suggestions/${managedEntityId}/not-now`, { performerId }, withAuth());
    },

    /**
     * Ask the workspace that keeps an Unclaimed act to send it the claim invite
     * (ADR-0042). The invite will name this workspace as having suggested it.
     */
    async suggestInvite(
        managedEntityId: string,
        args: { performerId: string; email: string; phone?: string },
    ): Promise<{ state: 'waiting'; maintainerName: string }> {
        const res = await axios.post(`${API_BASE}/suggestions/${managedEntityId}/invite-suggestions`, args, withAuth());
        return res.data;
    },

    async getSettings(initiatorId: string, recipientId: string): Promise<Record<string, any>> {
        const res = await axios.get(`${API_BASE}/settings`, {
            params: { initiatorId, recipientId },
            ...withAuth(),
        });
        return res.data.settings;
    },

    async updateSettings(
        initiatorId: string,
        recipientId: string,
        settings: Record<string, any>,
    ): Promise<any> {
        const res = await axios.put(
            `${API_BASE}/settings`,
            { initiatorId, recipientId, settings },
            withAuth(),
        );
        return res.data;
    },
};

export default connectionApi;
